import { create } from 'zustand'
import type { GraphDocument } from '../lib/documents'
import {
  getDocument,
  patchDocument,
  setShared as setSharedApi,
  rotateShareSlug as rotateApi,
} from '../lib/documents'
import type { JsonValue } from '../types'

export type SaveStatus = 'saved' | 'unsaved' | 'saving' | 'error'

type State = {
  doc: GraphDocument | null
  loading: boolean
  loadError: string | null
  saveStatus: SaveStatus
  saveError: string | null
  localVersion: number // bumped on any local edit; used to detect changes-during-save
  lastSavedVersion: number
}

type Actions = {
  load: (id: string) => Promise<void>
  clear: () => void
  markDirty: () => void
  save: (content: JsonValue) => Promise<void>
  setTitle: (title: string) => Promise<void>
  setShared: (enabled: boolean) => Promise<void>
  rotateSlug: () => Promise<void>
}

export const useDocumentStore = create<State & Actions>()((set, get) => ({
  doc: null,
  loading: false,
  loadError: null,
  saveStatus: 'saved',
  saveError: null,
  localVersion: 0,
  lastSavedVersion: 0,

  load: async (id) => {
    set({ loading: true, loadError: null, doc: null, saveStatus: 'saved', localVersion: 0, lastSavedVersion: 0 })
    try {
      const doc = await getDocument(id)
      if (!doc) {
        set({ loading: false, loadError: 'Document not found' })
        return
      }
      set({ doc, loading: false })
    } catch (e) {
      set({ loading: false, loadError: (e as Error).message })
    }
  },

  clear: () => {
    set({
      doc: null,
      loading: false,
      loadError: null,
      saveStatus: 'saved',
      saveError: null,
      localVersion: 0,
      lastSavedVersion: 0,
    })
  },

  markDirty: () => {
    set((s) => ({
      localVersion: s.localVersion + 1,
      saveStatus: s.saveStatus === 'saving' ? 'saving' : 'unsaved',
    }))
  },

  save: async (content) => {
    const { doc, localVersion } = get()
    if (!doc) return
    const versionAtStart = localVersion
    set({ saveStatus: 'saving', saveError: null })
    try {
      const updated = await patchDocument(doc.id, { content })
      set((s) => ({
        doc: { ...s.doc!, ...updated },
        lastSavedVersion: versionAtStart,
        saveStatus: s.localVersion === versionAtStart ? 'saved' : 'unsaved',
      }))
    } catch (e) {
      set({ saveStatus: 'error', saveError: (e as Error).message })
    }
  },

  setTitle: async (title) => {
    const { doc } = get()
    if (!doc) return
    // optimistic
    set({ doc: { ...doc, title } })
    try {
      const updated = await patchDocument(doc.id, { title })
      set((s) => ({ doc: { ...s.doc!, ...updated } }))
    } catch (e) {
      set({ saveError: (e as Error).message })
    }
  },

  setShared: async (enabled) => {
    const { doc } = get()
    if (!doc) return
    const updated = await setSharedApi(doc.id, enabled)
    set({ doc: updated })
  },

  rotateSlug: async () => {
    const { doc } = get()
    if (!doc) return
    const updated = await rotateApi(doc.id)
    set({ doc: updated })
  },
}))
